import {
  CheckCircleOutlined,
  CheckOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  PlusOutlined
} from '@ant-design/icons';
import {
  Button,
  Card,
  List,
  Modal,
  Result,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd';
import * as moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import Cards from 'react-credit-cards';
import 'react-credit-cards/es/styles-compiled.css';
import { Title } from '../../components';
import Pagos360Form from '../../components/Pagos360Form';
import ResponsiveContainer from '../../components/ResponsiveContainer';
import UserContext from '../../context/user-context';
import {
  LABEL_ESTADOS_ERROR_TRANSACCIONES_BANCARD,
  errorHandler
} from '../../utils';
import { instanceRef } from '../../utils/axios';
import formatoGs from '../../utils/converters';

const { Paragraph } = Typography;

function PolizaEstadoView(props) {
  const { user } = useContext(UserContext);

  const [fields, setFields] = useState();
  const [loading, setLoading] = useState();
  const [pagando, setPagando] = useState();
  const [errorPago, setErrorPago] = useState();
  const [catastro, setCatastro] = useState();
  const [tarjetas, setTarjetas] = useState([]);
  const [tarjetasVisible, setTarjetasVisible] = useState();
  const [eliminandoTarjeta, setEliminandoTarjeta] = useState(false);
  const [card, setCard] = useState();
  const isAdmin = user?.['cognito:groups']?.includes('ADMIN');

  const getTipo = (tipo) => {
    switch (tipo) {
      case 'VI':
        return 'visa';
      case 'VD':
        return 'visa';
      case 'MC':
        return 'mastercard';
      case 'AE':
        return 'amex';
      case 'MD':
        return 'mastercard';

      default:
        return tipo;
    }
  };
  const eliminarTarjeta = async (idCliente, idTarjeta) => {
    try {
      setEliminandoTarjeta(true);
      await instanceRef.instance.delete(
        `pagos/cliente/${idCliente}/tarjetas/${idTarjeta}`
      );
      setCard();
      message.info('Tarjeta eliminada correctamente');
      if (idTarjeta === card?.id) {
        setLastCard(idCliente);
      } else {
        const nuevoListado = tarjetas.filter((item) => item.id !== idTarjeta);
        setTarjetas(nuevoListado);
        if (nuevoListado.length === 0) {
          setCatastro(true);
          setTarjetasVisible(false);
        }
      }
    } catch (error) {
      message.error('No se pudo eliminar la tarjeta');
    }
    setEliminandoTarjeta(false);
  };

  const getClienteTarjetas = (id) => {
    return instanceRef.instance.get(`pagos/cliente/${id}/tarjetas`);
  };

  const getEstadoLabel = (estado, mensaje) => {
    if (estado === 'OK')
      return (
        <Tag color="green" key={estado}>
          {estado}
        </Tag>
      );
    if (estado === 'ERROR') {
      return (
        <Tooltip title={mensaje}>
          <Tag color="red" key={estado}>
            {estado}
          </Tag>
        </Tooltip>
      );
    }
    return (
      <Tag color="orange" key="PENDIENTE">
        PENDIENTE
      </Tag>
    );
  };

  const setLastCard = async (idCliente, paymentMethodId) => {
    const tarjetasResponse = await getClienteTarjetas(idCliente);
    setTarjetas(tarjetasResponse.data.data);
    if (
      !tarjetasResponse.data.data ||
      tarjetasResponse.data.data.length === 0
    ) {
      setCatastro(true);
      setTarjetasVisible(false);
    } else {
      let cardSelected = {};
      if (paymentMethodId) {
        cardSelected = tarjetasResponse.data.data.find(
          (item) => item.id === paymentMethodId
        );
      }
      if (!cardSelected || !cardSelected.card_info) {
        cardSelected =
          tarjetasResponse.data.data[tarjetasResponse.data.data.length - 1];
      }
      const cardInfo = JSON.parse(cardSelected.card_info);

      setCard({
        id: cardSelected.id,
        cvc: '',
        expiry: `${cardInfo.expiration_month}/${cardInfo.expiration_year}`,
        name: cardInfo.holder_name,
        number: cardInfo.last4,
        issuer: getTipo(cardInfo.brand)
      });
    }
  };

  const fetch = async () => {
    setLoading(true);
    try {
      const response = await instanceRef.instance.get(
        `/poliza/${props.match.params.id}`
      );
      setFields(response.data.data);
      if (
        response.data.data.persona.id_cliente &&
        response.data.data.solicitud.metodoPago.codigo === 'tarjeta'
      ) {
        await setLastCard(
          response.data.data.persona.id_cliente,
          response.data.data.payment_method_client_id
        );
      }
      if (response.data.data) {
        const cuotaError = response.data.data.pagos.find(
          (item) =>
            item.estado === 'FONDOS_INSUFICIENTES' ||
            item.estado === 'TARJETA_INHABILITADA' ||
            item.estado === 'TARJETA_INVALIDA' ||
            item.estado === 'TRANSACCION_INVALIDA' ||
            item.estado === 'ERROR'
        );
        if (cuotaError) {
          setErrorPago(cuotaError);
        } else {
          setErrorPago(undefined);
        }
      } else {
        setErrorPago(undefined);
      }
    } catch (err) {
      message.error('Error al obtener datos de la póliza');
    }
    setLoading(false);
  };

  const sort = (array) =>
    array.sort((a, b) => {
      if (a.cuota_numero > b.cuota_numero) {
        return 1;
      }
      return b.cuota_numero > a.cuota_numero ? -1 : 0;
    });

  const getIndicador = ({ estado }) => {
    if (estado === 'PAGADO') {
      return <CheckCircleOutlined style={{ fontSize: 20, color: '#52c41a' }} />;
    }
    if (estado !== 'PENDIENTE') {
      return (
        <ExclamationCircleOutlined style={{ fontSize: 20, color: '#ff4d4f' }} />
      );
    }
    return null;
  };

  const pagar = async () => {
    try {
      setPagando(true);
      await instanceRef.instance.post(`/pagos/pagar/${errorPago.id_deuda}`, {
        payment_method_client_id: card.id
      });
      setErrorPago(undefined);
      await fetch();
      message.success('Pago realizado correctamente');
    } catch (err) {
      errorHandler(err);
    }
    setPagando(false);
  };

  const columns = [
    {
      title: '',
      key: 'estado',
      width: '32px',
      render: getIndicador
    },
    {
      title: 'Nro',
      dataIndex: 'cuota_numero',
      key: 'cuota_numero',
      width: '40px'
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      key: 'monto',
      render: (monto) => (
        <div style={{ whiteSpace: 'nowrap' }}>{formatoGs(monto)}</div>
      ),
      align: 'right'
    },
    {
      title: 'Vencimiento',
      dataIndex: 'fecha_vencimiento',
      key: 'fecha_vencimiento',
      render: (fecha_vencimiento) =>
        moment(fecha_vencimiento).format('DD-MM-YYYY')
    },
    {
      title: 'Fecha Pago',
      dataIndex: 'fecha_pago',
      key: 'fecha_pago',
      render: (fecha_pago) =>
        fecha_pago ? moment(fecha_pago).format('DD-MM-YYYY') : '...'
    },
    {
      title: 'Detalle',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => {
        return LABEL_ESTADOS_ERROR_TRANSACCIONES_BANCARD[estado];
      },
      width: '30%'
    },
    {
      title: 'Detalle extendido',
      dataIndex: 'transaction_info',
      key: 'transaction_info',
      render: (transaction_info) => {
        let detalleToUser = '';
        if (transaction_info && user) {
          const {
            messageCode = '',
            messageDescription = '',
            extendMessageDescription = ''
          } = transaction_info;

          if (messageDescription) {
            detalleToUser =
              isAdmin && extendMessageDescription
                ? `Código: ${messageCode} - Descripción: ${messageDescription} - Extendido: ${extendMessageDescription}`
                : `Descripción: ${messageDescription}`;
          }
        }
        return detalleToUser;
      },
      width: '30%'
    },
    {
      title: 'Comprobante',
      dataIndex: 'comprobante',
      key: 'comprobante',
      width: '10%'
    },
    ...(isAdmin
      ? [
          {
            title: 'Estado Sistema Core',
            dataIndex: 'sebaot_notificacion_estado',
            key: 'sebaot_notificacion_estado',
            render: (sebaot_notificacion_estado, record) => {
              const mensaje = record.sebaot_notificacion_mensaje;
              return getEstadoLabel(sebaot_notificacion_estado, mensaje);
            }
          },
          {
            title: 'Estado Pagos',
            dataIndex: 'pagos_notificacion_estado',
            key: 'pagos_notificacion_estado',
            render: (pagos_notificacion_estado, record) => {
              const mensaje = record.pagos_notificacion_mensaje;
              return getEstadoLabel(pagos_notificacion_estado, mensaje);
            }
          }
        ]
      : [])
  ];

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ResponsiveContainer size="large">
      <Spin spinning={loading}>
        <Card
          size="small"
          title={`Póliza Nro.: ${
            fields?.numero
              ? `${fields.seccion || ''}-${fields.numero || ''}-${
                  fields.endoso === null ? '' : fields.endoso
                }`
              : 'SIN NÚMERO'
          }`}
          style={{ textAlign: 'left' }}
        >
          {/* <iframe
          style={{ width: '100%', height: '100vh' }}
          src="http://poliza.alianzagarantia.com:8075/3D02"
          title="Poliza"
        /> */}
          {fields && (
            <>
              {fields.estado === 'PENDIENTE' && (
                <Result
                  status="warning"
                  title="¡Hay problemas con su póliza!"
                  subTitle="Verificar"
                  extra={<Button type="primary">Verificar</Button>}
                />
              )}
              {fields.estado === 'VENCIDO' && (
                <Result
                  status="warning"
                  title="¡Póliza Vencida!"
                  subTitle="La póliza se encuentra vencida a la fecha"
                  extra={<Button type="primary">Verificar</Button>}
                />
              )}
              {fields.estado === 'ANULADO' && (
                <Result
                  status="error"
                  title="¡Póliza Anulada!"
                  subTitle="La póliza se encuentra anulada"
                  extra={<Button type="primary">Verificar</Button>}
                />
              )}
              <div align="middle">
                {fields.solicitud?.metodoPago?.codigo === 'tarjeta' &&
                  card &&
                  !catastro && (
                    <Space size="large">
                      <>
                        {errorPago && (
                          <>
                            <Result
                              status="warning"
                              title={
                                LABEL_ESTADOS_ERROR_TRANSACCIONES_BANCARD[
                                  errorPago?.estado
                                ]
                              }
                              subTitle={`¡Hay problemas con el pago de su cuota ${
                                errorPago?.cuota_numero || ''
                              }/${fields?.total_cuotas}!`}
                              extra={[
                                <Tooltip
                                  title={!card && 'Seleccione la tarjeta'}
                                >
                                  <Button
                                    type="primary"
                                    loading={pagando}
                                    disabled={!card}
                                    onClick={() => {
                                      pagar();
                                    }}
                                  >
                                    Reintentar pago
                                  </Button>
                                </Tooltip>
                              ]}
                            />
                          </>
                        )}
                      </>
                      <div>
                        <Title>Tarjeta actual</Title>
                        <br />
                        <Cards
                          cvc={card.cvc}
                          expiry={card.expiry}
                          name={card.name || ' '}
                          number={card.number}
                          issuer={card.issuer}
                          preview
                          locale={{ valid: 'Válido hasta' }}
                          placeholders={{ name: '' }}
                        />
                        <br />
                        <Button
                          type="primary"
                          ghost={errorPago}
                          disabled={pagando}
                          onClick={() => {
                            setTarjetasVisible(true);
                            // setCard();
                          }}
                        >
                          Usar otra
                        </Button>
                      </div>
                    </Space>
                  )}

                {fields.solicitud?.metodoPago?.codigo === 'tarjeta' &&
                  catastro && (
                    <>
                      <Title>Catastro de tarjeta</Title>
                      <div align="middle">
                        <div style={{ maxWidth: 500 }}>
                          <Pagos360Form
                            config={{
                              clientId: fields.persona.id_cliente,
                              debtId: errorPago?.id_deuda
                            }}
                            type="bancard"
                            onSuccess={() => {
                              message.success(
                                'Tarjeta catastrada correctamente'
                              );
                              setCatastro(false);
                              setLastCard(fields.persona.id_cliente);
                            }}
                            onError={(data) => {
                              if (
                                data.error &&
                                data.error.message &&
                                data.error.message.code === 5001
                              ) {
                                message.error(
                                  `Error al catastrar tarjeta: Verificar documento`
                                );
                              } else {
                                message.error(
                                  `Error al catastrar tarjeta: ${JSON.stringify(
                                    data
                                  )}`
                                );
                              }
                            }}
                          />
                        </div>
                      </div>
                      <br />
                      {tarjetas.length > 0 ? (
                        <Button
                          onClick={() => {
                            setCatastro(false);
                            setTarjetasVisible(true);
                          }}
                        >
                          Usar existente
                        </Button>
                      ) : (
                        'No tienes ninguna tarjeta catastrada'
                      )}
                    </>
                  )}
                {
                  <Modal
                    title="Seleccione su tarjeta"
                    open={tarjetasVisible}
                    closable={false}
                    width={600}
                    align="middle"
                    footer={[
                      <Button
                        icon={<PlusOutlined />}
                        type="primary"
                        ghost
                        key="agregar"
                        // style={{ color: 'inherit' }}
                        onClick={() => {
                          setCatastro(true);
                          setTarjetasVisible(false);
                        }}
                      >
                        Agregar nueva
                      </Button>,
                      <Button
                        key="cancelar"
                        style={{ color: 'inherit' }}
                        onClick={() => {
                          setTarjetasVisible(false);
                        }}
                      >
                        Cancelar
                      </Button>
                    ]}
                  >
                    <List
                      size="small"
                      itemLayout="vertical"
                      dataSource={tarjetas.map((item) => {
                        const cardInfo = JSON.parse(item.card_info);
                        const innerCard = {
                          id: item.id,
                          cvc: '123',
                          expiry: `${cardInfo.expiration_month}/${cardInfo.expiration_year}`,
                          name: cardInfo.holder_name,
                          number: cardInfo.last4,
                          issuer: getTipo(cardInfo.brand),
                          last4: cardInfo.last4
                        };
                        return innerCard;
                      })}
                      renderItem={(item) => (
                        <Card
                          actions={[
                            <Button
                              type="link"
                              icon={<CheckOutlined />}
                              disabled={eliminandoTarjeta}
                              onClick={() => {
                                setCard(item);
                                setTarjetasVisible(false);
                                setCatastro(false);
                              }}
                            />,
                            <Button
                              type="text"
                              icon={<DeleteOutlined />}
                              loading={eliminandoTarjeta}
                              onClick={() =>
                                eliminarTarjeta(
                                  fields.persona.id_cliente,
                                  item.id
                                )
                              }
                            />
                          ]}
                          style={{ marginBottom: 20 }}
                          size="small"
                        >
                          <List.Item
                            extra={
                              <div className="small">
                                <Cards
                                  cvc={item.cvc}
                                  expiry={item.expiry}
                                  name={item.name || ' '}
                                  number={item.number}
                                  issuer={item.issuer}
                                  preview
                                  locale={{ valid: 'Válido hasta' }}
                                  placeholders={{ name: '' }}
                                />
                              </div>
                            }
                          >
                            <List.Item.Meta
                              title={
                                <span style={{ textTransform: 'uppercase' }}>
                                  {`${item.issuer}`}
                                </span>
                              }
                              description={`${item.last4}`}
                            />
                          </List.Item>
                        </Card>
                      )}
                    />
                  </Modal>
                }
              </div>

              <div style={{ overflow: 'auto' }}>
                <Table
                  title={() => <Title>Detalle de cuotas</Title>}
                  // bordered
                  style={{ width: '100%', tableLayout: 'inherit !important' }}
                  size="small"
                  columns={columns}
                  dataSource={sort(fields.pagos)}
                  pagination={false}
                  scroll={false}
                  rowKey="cuota_numero"
                  tableLayout="auto"
                  rowClassName="expandable-row table-small"
                  // rowClassName="table-small"
                  expandable={{
                    // expandIconColumnIndex: 0,
                    expandRowByClick: true,
                    columnWidth: '90px',
                    fixed: 'left',
                    expandIcon: ({ expanded, onExpand, record }) =>
                      expanded ? (
                        <Button
                          type="text"
                          style={{ width: 80 }}
                          onClick={(e) => onExpand(record, e)}
                        >
                          Cerrar
                        </Button>
                      ) : (
                        record.comprobante && (
                          <Button
                            type="text"
                            style={{ width: 80 }}
                            onClick={(e) => onExpand(record, e)}
                          >
                            Ver
                          </Button>
                        )
                      ),
                    expandedRowRender: ({
                      comprobante,
                      transaction_info,
                      sebaot_notificacion_fecha,
                      pagos_notificacion_fecha
                    }) => (
                      <>
                        {comprobante && (
                          <Result
                            status="success"
                            icon={<CheckCircleOutlined />}
                            title="¡La cuota se encuentra pagada!"
                            subTitle={`Comprobante nro: ${comprobante}`}
                          >
                            <div className="desc">
                              <Paragraph>
                                Fecha:{' '}
                                {moment(transaction_info.date).format(
                                  'DD/MM/YYYY HH:mm'
                                )}
                              </Paragraph>
                              <Paragraph>
                                Número de pedido:{' '}
                                {transaction_info.shop_process_id}
                              </Paragraph>
                              <Paragraph>
                                Importe:{' '}
                                {formatoGs(
                                  // TODO: Consultar con Camilo, entiendo que son datos de Pagos360
                                  Number(
                                    transaction_info.amount.replace(',', '.')
                                  )
                                )}
                              </Paragraph>
                              <Paragraph>
                                Descripción:{' '}
                                {transaction_info.response_description
                                  ? transaction_info.response_description
                                  : ''}{' '}
                                {transaction_info.messageDescription
                                  ? transaction_info.messageDescription
                                  : ''}
                              </Paragraph>
                              {user &&
                                user['cognito:groups'].includes('ADMIN') &&
                                transaction_info.extendMessageDescription && (
                                  <Paragraph>
                                    Detalle:{' '}
                                    {`Código: ${transaction_info.messageCode} - Descripción: ${transaction_info.messageDescription} - Extendido: ${transaction_info.extendMessageDescription}`}
                                  </Paragraph>
                                )}
                              {user &&
                                user['cognito:groups'].includes('ADMIN') && (
                                  <Paragraph>
                                    Fecha Notificación Sistema Core:{' '}
                                    {sebaot_notificacion_fecha
                                      ? moment(
                                          sebaot_notificacion_fecha
                                        ).format('DD/MM/YYYY HH:mm')
                                      : 'Pendiente'}
                                  </Paragraph>
                                )}
                              {user &&
                                user['cognito:groups'].includes('ADMIN') && (
                                  <Paragraph>
                                    Fecha Notificación Pagos:{' '}
                                    {pagos_notificacion_fecha
                                      ? moment(pagos_notificacion_fecha).format(
                                          'DD/MM/YYYY HH:mm'
                                        )
                                      : 'Pendiente'}
                                  </Paragraph>
                                )}
                            </div>
                          </Result>
                        )}
                      </>
                    ),
                    rowExpandable: (record) => record.comprobante
                  }}
                />
              </div>
            </>
          )}
        </Card>
      </Spin>
    </ResponsiveContainer>
  );
}

export default PolizaEstadoView;
